/**
 * 系统授权 Tab（管理员）
 * 显示本机机器码、授权状态/详情，支持导入授权、生成授权申请、移除授权。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { parseUTC } from '../utils/datetime';
import {
  KeyIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { systemLicenseApi, getErrorMessage } from '../services/api';

interface LicenseStatus {
  status: string;
  machine_id: string;
  message: string;
  license: {
    licensee?: string;
    edition?: string;
    issued_at?: string;
    expires_at?: string;
    features?: string[];
    limits?: Record<string, number>;
    remark?: string;
  } | null;
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  none: { label: '未授权', cls: 'bg-gray-100 text-gray-600' },
  valid: { label: '已授权·有效', cls: 'bg-green-100 text-green-700' },
  expired: { label: '已过期', cls: 'bg-red-100 text-red-700' },
  mismatch: { label: '机器码不匹配', cls: 'bg-orange-100 text-orange-700' },
  tampered: { label: '文件被篡改', cls: 'bg-red-100 text-red-700' },
};

const fmtExpire = (s?: string) => (!s ? '-' : s === 'perpetual' ? '永久' : s);

const SystemLicenseTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [st, setSt] = useState<LicenseStatus | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [importText, setImportText] = useState('');
  const [company, setCompany] = useState('');
  const [contact, setContact] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemLicenseApi.getStatus();
      setSt(res.data);
    } catch (e) {
      setMsg({ type: 'error', text: getErrorMessage(e, '加载失败') });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const status = st?.status || 'none';
  const meta = STATUS_META[status] || STATUS_META.none;
  const lic = st?.license;

  const doImport = async (content: string) => {
    if (!content.trim()) {
      setMsg({ type: 'error', text: '请粘贴或上传授权文件内容' });
      return;
    }
    setLoading(true);
    try {
      const res = await systemLicenseApi.import(content.trim());
      setMsg({ type: res.data.ok ? 'success' : 'error', text: res.data.message || '完成' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: getErrorMessage(e, '导入失败') });
    } finally {
      setLoading(false);
    }
  };

  const onFile = (f: File | undefined) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setImportText(String(r.result || ''));
    r.readAsText(f);
  };

  const doRequest = async () => {
    try {
      const res = await systemLicenseApi.request({ company, contact });
      const blob = new Blob([JSON.stringify(res.data.request, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'license-request.json';
      a.click();
      URL.revokeObjectURL(a.href);
      setMsg({ type: 'success', text: '授权申请文件已生成并下载，请发给授权方' });
    } catch (e) {
      setMsg({ type: 'error', text: getErrorMessage(e, '生成失败') });
    }
  };

  const doRemove = async () => {
    if (!window.confirm('确定移除当前授权？移除后功能将受限。')) return;
    try {
      await systemLicenseApi.remove();
      await load();
      setMsg({ type: 'success', text: '已移除授权' });
    } catch (e) {
      setMsg({ type: 'error', text: getErrorMessage(e, '移除失败') });
    }
  };

  const copyMid = async () => {
    try {
      await navigator.clipboard.writeText(st?.machine_id || '');
      setMsg({ type: 'success', text: '机器码已复制' });
    } catch {
      setMsg({ type: 'error', text: '复制失败，请手动选择复制' });
    }
  };

  let daysLeft: number | null = null;
  if (status === 'valid' && lic?.expires_at && lic.expires_at !== 'perpetual') {
    daysLeft = Math.ceil(((parseUTC(lic.expires_at) ?? new Date()).getTime() - Date.now()) / 86400000);
  }

  return (
    <section>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900">软件授权（私有化 License）</h3>
        <p className="text-xs text-gray-500 mt-0.5">基于机器码的离线授权。复制机器码给授权方 → 获取授权文件 → 导入激活。</p>
      </div>

      {msg && (
        <div className={`mb-3 px-4 py-2.5 rounded-lg text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {msg.text}
        </div>
      )}

      {loading && !st ? (
        <div className="text-center py-12 text-sm text-gray-400">加载中...</div>
      ) : (
        <div className="space-y-5">
          {/* 本机机器码 */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 mb-1">本机机器码（发给授权方）</div>
                <div className="font-mono text-base font-semibold text-gray-900 tracking-wider">{st?.machine_id || '-'}</div>
              </div>
              <button onClick={copyMid} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100">
                <ClipboardDocumentIcon className="w-4 h-4 mr-1" />复制
              </button>
            </div>
          </div>

          {/* 授权状态 */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              {status === 'valid' ? (
                <ShieldCheckIcon className="w-6 h-6 text-green-600" />
              ) : (
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-500" />
              )}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${meta.cls}`}>{meta.label}</span>
              <span className="text-xs text-gray-500">{st?.message}</span>
              {daysLeft !== null && (
                <span className={`text-xs ${daysLeft <= 30 ? 'text-red-600' : 'text-gray-500'}`}>剩余 {daysLeft} 天</span>
              )}
            </div>
            {lic && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-gray-500">授权对象：</span>{lic.licensee || '-'}</div>
                <div><span className="text-gray-500">版本：</span>{lic.edition || '-'}</div>
                <div><span className="text-gray-500">签发日期：</span>{lic.issued_at || '-'}</div>
                <div><span className="text-gray-500">到期日期：</span>{fmtExpire(lic.expires_at)}</div>
                <div className="col-span-2"><span className="text-gray-500">功能：</span>{(lic.features && lic.features.length) ? lic.features.join('、') : '不限制'}</div>
                <div className="col-span-2"><span className="text-gray-500">额度：</span>
                  {lic.limits && Object.keys(lic.limits).length
                    ? Object.entries(lic.limits).map(([k, v]) => `${k}=${v}`).join('，')
                    : '不限制'}
                </div>
                {lic.remark ? <div className="col-span-2 text-gray-500">备注：{lic.remark}</div> : null}
              </div>
            )}
          </div>

          {/* 导入授权 */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-800 mb-2">导入授权文件</div>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="粘贴 license.lic 文件内容（JSON）"
              className="w-full h-24 px-3 py-2 text-xs font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
            />
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center px-3 py-1.5 text-sm rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer">
                选择文件
                <input type="file" accept=".lic,.json,application/json" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </label>
              <button onClick={() => doImport(importText)} disabled={loading} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                <KeyIcon className="w-4 h-4 mr-1" />导入并激活
              </button>
              {lic && (
                <button onClick={doRemove} className="ml-auto inline-flex items-center px-3 py-1.5 text-sm rounded-md text-red-600 hover:bg-red-50">
                  <TrashIcon className="w-4 h-4 mr-1" />移除授权
                </button>
              )}
            </div>
          </div>

          {/* 授权申请 */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-800 mb-2">申请授权（无授权文件时）</div>
            <div className="flex items-center gap-2 mb-2">
              <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="公司名称" className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="联系人/方式(选填)" className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button onClick={doRequest} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100">
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />生成授权申请文件
            </button>
            <p className="text-xs text-gray-400 mt-1">生成含机器码的申请文件，下载后发给授权方；授权方用其私钥签发 license.lic 回给你。</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default SystemLicenseTab;
